import { GTM } from '../utils/gtm'
import { isIOSWebview } from '../utils/isIOSWebview'
import { detectFeature } from './detectFeature'
import type { TSwMessage } from './messages'

type TAsyncRetryOptions = {
  /** The maximum number of times an attempt to resolve the promise will be performed (default to 3) */
  attempts?: number
  /** Optional delay between attempts (default to 0) */
  delayBetweenAttempts?: number
}

type TAsyncWithTimeoutOptions = {
  /** The time given to the promise to resolve before timeout */
  attemptTime: number
  /** Optional timeout error message (default to 'Timeout') */
  timeoutMessage?: string
}

type TAsyncOptions = {
  extras?: TAnyProps
  rethrow?: boolean
  errorHandling?: TAsyncRetryOptions & TAsyncWithTimeoutOptions
}

type TMessageSomeSWParams = {
  getSw: () => Promise<ServiceWorker | null | undefined>
  swName: string
  message: TSwMessage
  options?: TAsyncOptions
}

const messageSomeSW = async ({ getSw, swName, message, options }: TMessageSomeSWParams) => {
  try {
    console.debug(`📨 Messaging ${swName} service worker.`, message)
    const sw = await getSw()
    if (sw) {
      const channel = new MessageChannel()

      sw.postMessage(message, [channel.port2])

      return await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject('Service worker response timed out')
        }, 60000)

        const handleMessage = (event: MessageEvent) => {
          channel.port1.removeEventListener('message', handleMessage)
          const { data } = event
          console.debug('📥 Response received from service worker.', data)
          if (data?.error) {
            console.error(data?.error)
            clearTimeout(timeoutId)
            reject(data.error)
          } else {
            resolve(data)
          }
        }
        channel.port1.addEventListener('message', handleMessage)
      })
    } else {
      throw new Error(
        `Cannot send message (type: ${message.type}) to ${swName} service worker: service worker undefined`,
      )
    }
  } catch (error) {
    console.error('❌ Error while sending a message to the service worker.', { error, message, swName, options })
    if (options?.rethrow) {
      throw error
    } else {
      console.warn(`Error while sending a ${message.type} message to the ${swName} service worker.`)
      return undefined
    }
  }
}

/**
 * Utilities for sending a message to a specific Service Worker using postMessage.
 *
 * - `controller`: sends a message to the currently active Service Worker (the one controlling the page).
 * - `waiting`: sends a message to the waiting Service Worker (if one exists).
 *
 * Uses `messageSomeSW`, a generic utility function that handles asynchronous messaging
 * with support for custom options (timeout, retries, etc.).
 *
 * @template T - The expected response type from the service worker.
 * @param message - The message
 */
export const sendMessageToSW = {
  controller: (message: TSwMessage, options?: TAsyncOptions) =>
    messageSomeSW({ getSw: async () => navigator.serviceWorker.controller, swName: 'controller', message, options }),
  waiting: (message: TSwMessage, options?: TAsyncOptions) =>
    messageSomeSW({
      getSw: async () => {
        const registration = await navigator.serviceWorker.getRegistration()
        return registration?.waiting
      },
      swName: 'waiting',
      message,
      options,
    }),
}

export function getNotificationsAvailable() {
  if (isIOSWebview()) {
    return false
  }
  return detectFeature('serviceworker') && detectFeature('notifications') && detectFeature('push')
}

export const unregisterAllServiceWorkers = async () => {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations()
    return (await Promise.all(registrations.map((registration) => registration.unregister()))).every(Boolean)
  } catch (error) {
    console.warn('❌ Error while unregistering all service workers', error)
    return false
  }
}

export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('❌ Service Worker not supported')
    return { registration: undefined }
  }

  try {
    console.debug('📞 Registering service worker...')
    const registration = await navigator.serviceWorker.register('/sw.js')

    console.debug('✅ Service worker registered.', { registration })

    // Optional: Tracking
    GTM?.sendEvent?.({ serviceWorker: 'active' })

    return { registration }
  } catch (error) {
    console.error('❌ Failed to register SW.', error)

    GTM?.sendEvent?.({ serviceWorker: 'inactive' })

    return { registration: undefined }
  }
}
